import { useState } from "react";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Download, FileArchive } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { parseYoutubeId, THUMB_SIZES, thumbUrl, isPlaceholder, isVideoNotFound, availableSizes, allSettled, buildThumbZip, previewSize, thumbFileName, thumbZipName } from "./ytUtils";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Downloads the thumbnail image of a YouTube video in every size YouTube has, given the video's address. Works with watch, youtu.be, Shorts, embed, live and mobile links.`,
  how: `Paste the YouTube link (or just the 11-character video ID) and press Get Thumbnails. A large preview shows the biggest size that exists, sizes YouTube doesn't have for that video are left out, and a video ID that does not exist shows "Video not found or has no thumbnail". Press Download on one size, or Download all sizes (ZIP) for every size that exists in one file.`,
  tip: `The largest size (1280×720) only exists for videos uploaded in HD, and the "Standard" size has black bars above and below on widescreen videos. For most uses the max resolution is the one to take; if it is unavailable, use High quality.`,
};

export default function YtThumbnail() {
  const [url, setUrl] = useState("");
  const [id, setId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [missing, setMissing] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState<Record<string, { w: number; h: number }>>({});
  const [zipping, setZipping] = useState(false);
  const { logUsageNow } = useLogUsageOnAction();

  const extract = () => {
    const found = parseYoutubeId(url);
    setMissing({});
    setLoaded({});
    if (!found) { setId(null); setError("That doesn't look like a YouTube video link. Paste the address of a video (watch, youtu.be, Shorts or embed link)."); return; }
    setError(""); setId(found);
  };

  const download = async (file: string) => {
    if (!id) return;
    // logUsageNow always answers: true, or it has already shown the pass dialog / a "couldn't check" message (B-0309).
    if (!(await logUsageNow())) return;
    const src = thumbUrl(id, file);
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error(String(res.status));
      downloadBlob(await res.blob(), thumbFileName(id, file));
    } catch {
      // The browser may block reading the image directly; opening it lets the person save it by hand.
      window.open(src, "_blank", "noopener,noreferrer");
      toast.info("Your browser blocked the direct download, so the image opened in a new tab. Right-click it and choose Save image.");
    }
  };

  const zipAll = async () => {
    if (!id || zipping) return;
    setZipping(true);
    try {
      // Built first, charged when the ZIP is actually saved (downloadBlob asks the trial gate at that moment).
      const { blob, included, failed } = await buildThumbZip(id, availableSizes(missing));
      downloadBlob(blob, thumbZipName(id));
      if (failed.length > 0) toast.info(`${failed.length} size${failed.length === 1 ? "" : "s"} couldn't be fetched and ${failed.length === 1 ? "is" : "are"} not in the ZIP. ${included.length} included.`);
    } catch {
      toast.error("Your browser blocked bundling the images, so no ZIP was made. Use the Download button on each size instead.");
    } finally { setZipping(false); }
  };

  const preview = id ? previewSize(missing, loaded) : null;
  const shown = availableSizes(missing);
  const hidden = THUMB_SIZES.filter(t => missing[t.file]);
  const settled = allSettled(missing, loaded);

  return (
    <ToolContainer>
      <div className="space-y-4">
        <form className="flex gap-3" onSubmit={e => { e.preventDefault(); extract(); }}>
          <div className="flex-1"><Label>YouTube URL</Label><Input aria-label="YouTube URL" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://youtube.com/watch?v=… or a Shorts / youtu.be link" /></div>
          <Button className="mt-6" type="submit">Get Thumbnails</Button>
        </form>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {id && isVideoNotFound(missing) && (
          <NoticeBox className="text-sm" testId="yt-not-found">Video not found or has no thumbnail. Check the link or video ID and try again.</NoticeBox>
        )}
        {id && !isVideoNotFound(missing) && (
          <div className="space-y-4">
            {preview && preview !== "loading" && (
              <div className="space-y-1" data-testid="yt-preview">
                <img src={thumbUrl(id, preview.file)} alt={`${preview.label} preview`} className="w-full max-w-2xl rounded-lg border" />
                <p className="text-xs text-muted-foreground">Preview: {preview.label}, {loaded[preview.file].w}×{loaded[preview.file].h}</p>
              </div>
            )}
            {preview === "loading" && <p className="text-sm text-muted-foreground" role="status">Loading preview…</p>}
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={zipAll} disabled={!settled || zipping || shown.length === 0} className="gap-1.5">
                <FileArchive className="h-4 w-4" /> {zipping ? "Making ZIP…" : settled ? `Download all sizes (ZIP, ${shown.length} files)` : "Checking sizes…"}
              </Button>
              {hidden.length > 0 && <p className="text-xs text-muted-foreground" data-testid="yt-hidden">YouTube doesn't have {hidden.map(t => t.label).join(", ")} for this video, so {hidden.length === 1 ? "it is" : "they are"} left out.</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {shown.map(t => (
                <div key={t.file} className="border rounded-lg overflow-hidden">
                  <img
                    src={thumbUrl(id, t.file)}
                    alt={`${t.label} thumbnail`}
                    className="w-full"
                    onLoad={e => {
                      const img = e.target as HTMLImageElement;
                      if (isPlaceholder(img.naturalWidth, t.w)) setMissing(m => ({ ...m, [t.file]: true }));
                      else setLoaded(l => ({ ...l, [t.file]: { w: img.naturalWidth, h: img.naturalHeight } }));
                    }}
                    onError={() => setMissing(m => ({ ...m, [t.file]: true }))}
                  />
                  <div className="p-2 flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">{t.label} · {t.w}×{t.h}</span>
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => download(t.file)}>
                      <Download className="h-3.5 w-3.5" /> Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
